import { useQuery } from '@tanstack/react-query';
import type { FeedPaper } from './useProjectFeed';
import { normalizePaperData } from '../paperAuthors';
import { supabase, toError } from '../supabase';
import { withTimeout } from '../asyncTimeout';
import { BILLING_PREVIEW_PAPER_LIMIT } from '../billingPreviewState';

const PREVIEW_DIGEST_REQUEST_TIMEOUT_MS = 15000;

const PREVIEW_DIGEST_SELECT = `
    rank_position,
    project_paper:project_paper_id (
        id, short_id, slug, paper_title, summary,
        relevance_score, relevance_class, matching_details,
        personalized_note, ready_at,
        project_paper_feedback (
            vote, downvote_reason, updated_at
        ),
        paper:paper_id (
            id, short_id, slug, title, abstract, authors, published_date,
            pdf_url, url, doi, source_id, source_paper_id, source_paper_version
        )
    )
`;

function toSingleRelation<T>(value: T | T[] | null | undefined): T | null {
    if (Array.isArray(value)) {
        return value[0] ?? null;
    }

    return value ?? null;
}

function toFeedPaper(row: any): FeedPaper | null {
    const projectPaper = toSingleRelation(row?.project_paper) as any;
    if (!projectPaper?.id || !projectPaper.paper) {
        return null;
    }

    const feedback = toSingleRelation(projectPaper.project_paper_feedback);

    return {
        ...projectPaper,
        paper: normalizePaperData(projectPaper.paper),
        project_paper_feedback: feedback,
    } as FeedPaper;
}

/**
 * Loads the immutable result digest used for the one-time preview. This keeps
 * the settings page, preview feed, and email aligned even if the project feed
 * receives more rows after the result digest was created.
 */
export function useBillingPreviewDigest(
    digestId: string | null | undefined,
    options: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: ['billing-preview-digest', digestId],
        queryFn: async () => {
            if (!digestId) {
                throw new Error('No preview digest ID');
            }

            const query = supabase
                .from('digest_papers')
                .select(PREVIEW_DIGEST_SELECT, { count: 'exact' })
                .eq('digest_id', digestId)
                .order('rank_position', { ascending: true })
                .limit(BILLING_PREVIEW_PAPER_LIMIT);

            const { data, count, error } = await withTimeout(
                query,
                PREVIEW_DIGEST_REQUEST_TIMEOUT_MS,
                'Loading the preview digest timed out. Please try again.'
            );

            if (error) {
                throw toError(error);
            }

            const papers = (data || [])
                .map(toFeedPaper)
                .filter((paper): paper is FeedPaper => Boolean(paper));

            return {
                papers,
                totalCount: Math.max(count ?? 0, papers.length),
            };
        },
        enabled: Boolean(digestId) && (options.enabled ?? true),
        retry: 1,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
    });
}
